<script lang="ts" setup>
import { computed, onBeforeMount, Ref, ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useFloating, offset } from "@floating-ui/vue";
import { X, ArrowLeft, ChevronDown, Info, Ban } from "lucide-vue-next";

// Router
import router from "~/router";
import Trans from "~/modules/translations";

// Utils
import { useI18n } from "vue-i18n";
import { BigNumber } from "ethers";
import { getCurrencySymbol } from "~/utils/currency";
import { useTracking } from "~/composables/useGtm";
import { roundToDecimals } from "~/utils/math";

// Components
import Modal from "@/components/modals/ModalContainer.vue";
import AuctionItem from "~/components/marketplace/AuctionItem.vue";
import useConvert from "~/composables/useConvert";
import CardDataFooterScore from "../card/CardDataFooterScore.vue";
import CardDataFooterSkeleton from "../card/CardDataFooterSkeleton.vue";
import CardDataFooterPrice from "../card/CardDataFooterPrice.vue";
import ModalDeposit from "./ModalDeposit.vue";
import LastBids from "../marketplace/ModalBidRecentBids.vue";
import LastSales from "../marketplace/ModalBidRecentSales.vue";

// Stores
import { NotifType, useStoreApp } from "~/stores/storeApp";
import { useStoreMarketplace } from "~/stores/storeMarketplace";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import usePOLPrice from "~/stores/POLprice";
import { useSecondaryMarketplace } from "~/stores/storeSecondaryMarketplace";

// Payments
import { useStripeStore } from "~/composables/useStripe";

// Types
import { MarketType, CardType, NftSkill, Currency } from "fungi-types";
import { PaymentMethod } from "~/api/auction/types";

const { t } = useI18n();
const storeMarketplace = useStoreMarketplace();
const storeUser = useStoreUserV2();
const storeApp = useStoreApp();
const secondaryMarketplace = useSecondaryMarketplace();
const gtm = useTracking();

// Dropdown Currency
const showCurrencyDropdown = ref(false);
const toggleCurrencyDropdown = () => (showCurrencyDropdown.value = !showCurrencyDropdown.value);
const currencyDropdownRef = ref(null);
const currencyDropdown = ref(null);
onClickOutside(currencyDropdown, () => (showCurrencyDropdown.value = false));
const { floatingStyles } = useFloating(currencyDropdownRef, currencyDropdown, {
  middleware: [offset(10)],
});

// Utils
const { getAuctionAmount, increaseMinBidPrice, formatAuctionPrice } = useConvert;
const polPrice = usePOLPrice();

// Getters
const auction = computed(() => storeMarketplace.selectedItem.auction);
const isLogged = computed(() => storeUser.isConnected);
const marketType = computed(() => storeMarketplace.selectedItem.auction?.marketType);
const isPrimaryMarket = computed(() => marketType.value !== MarketType.SECONDARY);

const bidAmount = computed(() => {
  if (!auction.value) return null;
  const card = storeMarketplace.selectedItem as NftSkill;
  return getAuctionAmount({
    usdcAmount: auction.value.lastBidAmountUsdc || auction.value.startUsdcAmount,
    weiAmount: auction.value.lastBidAmount || auction.value.startedAmount,
    scarcity: card.scarcity,
    marketType: isPrimaryMarket.value ? MarketType.PRIMARY : MarketType.SECONDARY,
  });
});

const minBidAmount = computed(() => {
  const prices = bidAmount.value;
  if (!auction.value || prices === null) return null;
  else if (auction.value.countOrders === 0 || auction.value.marketType === MarketType.SECONDARY) {
    return prices;
  }

  return increaseMinBidPrice({ prices });
});

// Modal title
const title = computed(() => {
  return isPrimaryMarket.value ? t("_payment.place-your-bid") : t("_payment.confirm-your-purchase");
});

// Bid or Buy confirmation Button
const bidOrBuyConfirmation = computed(() => {
  return isPrimaryMarket.value ? t("_payment.confirm-bid") : t("_payment.confirm-buy");
});

// Currency Symbol
const currencySymbol = computed(() => {
  if (paymentMethod.value === PaymentMethod.CREDIT_CARD) {
    return getCurrencySymbol(currency.value as Currency);
  } else if (storeUser.migrationToUSDCCompleted) {
    return Currency.USDC;
  } else {
    return Currency.WPOL;
  }
});

// Insufficient Crypto Funds
const insufficientCryptoFunds = computed(() => {
  if (paymentMethod.value === PaymentMethod.CREDIT_CARD) {
    return false;
  } else if (storeUser.migrationToUSDCCompleted) {
    return storeUser.usdcRemaining < purchaseInfo.value.finalAmount;
  } else {
    return storeUser.wpolRemaining < purchaseInfo.value.finalAmount;
  }
});

// Handle Deposit Modal
const showModalDeposit = ref(false);
const closeModalDeposit = () => (showModalDeposit.value = false);
const addFund = () => {
  showModalDeposit.value = true;
};

// Handle Payment Method
const currency = ref<Currency>(
  marketType.value === MarketType.SECONDARY
    ? Currency.WPOL
    : (storeUser.preferredCurrency ?? Currency.USD)
);

// Update Currency from Dropdown
const updateCreditCardCurrency = (value: Currency) => {
  showCurrencyDropdown.value = false;
  currency.value = value;
  polPrice.refreshRates();
};

// Sync the min amount with the current item
const isBelowMinAmount = computed(() => {
  const resp = {
    isBelow: false,
    minAmount: 0,
  };

  if (!auction.value) return resp;
  if (auction.value.marketType === MarketType.SECONDARY) {
    return resp;
  }

  let currencyValue = currency.value;
  if (currency.value === Currency.USDC) currencyValue = Currency.USD;

  if (bidAmountInSelectedCurrency.value < purchaseInfo.value.minAmount[currencyValue]) {
    resp.isBelow = true;
    resp.minAmount = purchaseInfo.value.minAmount[currencyValue];
  }

  return resp;
});

const errorCrypto = ref<null | string>(null);
const errorFiat = ref<null | string>(null);

// initAmounts
const bidAmountFiat = ref(0);

const fiatError = ref(false);
const creditsToUse = ref(0);

const paymentMethod = ref<PaymentMethod>(
  marketType.value === MarketType.SECONDARY
    ? PaymentMethod.CRYPTO
    : currency.value === Currency.WMATIC
      ? PaymentMethod.CRYPTO
      : PaymentMethod.CREDIT_CARD
);

const bidAmountInSelectedCurrency = computed(() => {
  let amount = 0;
  if (marketType.value === MarketType.SECONDARY && !storeUser.migrationToUSDCCompleted) {
    amount = bidAmountInWpol.value;
  } else if (paymentMethod.value === PaymentMethod.CREDIT_CARD && currency.value === Currency.EUR) {
    return bidAmountInEur.value;
  } else if (
    (paymentMethod.value === PaymentMethod.CREDIT_CARD && currency.value === Currency.USD) ||
    (paymentMethod.value === PaymentMethod.CRYPTO && storeUser.migrationToUSDCCompleted)
  ) {
    return bidAmountInUsd.value;
  } else if (paymentMethod.value === PaymentMethod.CRYPTO && !storeUser.migrationToUSDCCompleted) {
    return bidAmountInWpol.value;
  }
  return 0;
});

export type PurchaseInfo = {
  paymentMethod: PaymentMethod;
  minAmount: Record<Currency.WPOL | Currency.USD | Currency.EUR, number>;
  bidAmount: number;
  marketPlace: {
    isPrimary: boolean;
    type: MarketType;
  };
  credits: {
    amount: number;
    amountInCurrency: number;
    balance: number;
  };
  currency: Currency;
  finalAmount: number;
};

const purchaseInfo = computed(() => {
  const resp: PurchaseInfo = {
    paymentMethod: paymentMethod.value,
    minAmount: {
      [Currency.WPOL]: minBidAmount.value?.crypto.wpol || 0,
      [Currency.EUR]: minBidAmount.value?.fiat.eur || 0,
      [Currency.USD]: minBidAmount.value?.fiat.usd || 0,
    },
    marketPlace: {
      isPrimary: isPrimaryMarket.value,
      type: marketType.value as MarketType,
    },
    bidAmount: bidAmountInSelectedCurrency.value,
    credits: {
      amount: creditsInfo.value.amount,
      amountInCurrency: creditsInfo.value.amountInCurrency,
      balance: storeUser.creditBalance,
    },
    currency: currency.value,
    finalAmount: finalAmount.value,
  };
  return resp;
});

const updateBidsInAllCurrencies = (
  event: Event,
  currency: Currency.EUR | Currency.USD | Currency.WPOL
) => {
  const value = (event.target as HTMLInputElement).value;

  const prices = formatAuctionPrice(+value, currency);

  if (currency !== Currency.WPOL) bidAmountInWpol.value = prices.crypto.wpol;
  if (currency !== Currency.USD) bidAmountInUsd.value = prices.fiat.usd;
  if (currency !== Currency.EUR) bidAmountInEur.value = prices.fiat.eur;
};

// MODELS FOR LAST BID AMOUNTS IN DIFFERENT CURRENCIES
const bidAmountInWpol = ref<number>(Number(minBidAmount.value?.crypto.wpol) || 0);
const bidAmountInUsd = ref<number>(Number(minBidAmount.value?.fiat.usd) || 0);
const bidAmountInEur = ref<number>(Number(minBidAmount.value?.fiat.eur) || 0);

const disableBidInput = computed(
  () => storeMarketplace.bidInProgress || marketType.value === MarketType.SECONDARY
);

const handlePaymentMethodChange = (method: PaymentMethod) => {
  if (method !== PaymentMethod.CREDIT_CARD) {
    if (storeUser.migrationToUSDCCompleted) currency.value = Currency.USD;
    else currency.value = Currency.WPOL;
  } else currency.value = Currency.USD;
  paymentMethod.value = method;
};

const bidOrBuy = async () => {
  // #1 Check if amount is below the minimum amount
  if (isBelowMinAmount.value.isBelow) {
    const msg = t("_payment.bid-amount-to-low", {
      amount: bidAmountInSelectedCurrency.value,
      currency: currency.value.toUpperCase(),
    });
    handleErrorMsg({ message: msg });
    return;
  }

  if (isPrimaryMarket.value) {
    await handleConfirmBid();
  } else if (marketType.value === MarketType.SECONDARY) {
    await handleConfirmBuy();
  }
};

const handleConfirmBuy = async () => {
  storeMarketplace.bidInProgress = true;

  try {
    if (storeUser.hasSmartWallet) {
      console.log("Buying item with smart wallet");
      await secondaryMarketplace.buyItem_new(storeMarketplace.selectedItem as NftSkill);
    } else {
      console.log("Buying item with old method");
      await secondaryMarketplace.buyItem_old(storeMarketplace.selectedItem as NftSkill);
    }
    handleCloseModal();
  } catch (e: any) {
    handleErrorMsg(e);
  }
  storeMarketplace.bidInProgress = false;
};

const hasAuctionEnded = computed(() => {
  if (!auction.value) return false;
  const end = new Date(auction.value.endedAt);
  return end.getTime() <= new Date().getTime();
});

const handleConfirmBid = async () => {
  if (!auction.value) throw new Error("Auction not found");
  // #1 Check is the user is connected
  if (!storeUser.isConnected) return login();

  // #3 Check if the auction has ended
  if (hasAuctionEnded.value) {
    storeApp.displayNotification(
      NotifType.BID_FAILED,
      storeMarketplace.selectedItem as NftSkill,
      t("errors.auction.timeover")
    );
    return storeApp.setBidModalState(false);
  }

  // Force bid on wallet if finalAmount is 0 on credit card Method
  // Else if use the payment method selected
  if (paymentMethod.value === PaymentMethod.CREDIT_CARD && finalAmount.value > 0) {
    await bidWithStripe();
  } else {
    await bid();
  }
};

const handleErrorMsg = (error: any) => {
  if (paymentMethod.value === PaymentMethod.CREDIT_CARD) {
    errorFiat.value = error.message;
  } else {
    errorCrypto.value = error.message;
  }
};

async function login() {
  storeApp.setBidModalState(false);
  router.push(Trans.i18nRoute({ name: "SignIn" })).catch(console.error);
}

async function bid() {
  storeMarketplace.bidInProgress = true;
  errorCrypto.value = null;
  errorFiat.value = null;

  try {
    if (storeMarketplace.selectedItem.auction) {
      await storeMarketplace.bidOnCard({
        item: storeMarketplace.selectedItem as NftSkill,
        bidAmountWpol: purchaseInfo.value.bidAmount,
        bidAMountUsdc: bidAmountInUsd.value,
        creditAmount: creditsToUse.value || 0,
        finalAmount: finalAmount.value,
      });
    }

    // storeApp.setBidModalState(false);
  } catch (error: any) {
    handleErrorMsg(error);
  }
  storeMarketplace.bidInProgress = false;
  handleCloseModal();
}

const checkoutRef = ref<HTMLElement>();
const stripe = useStripeStore();

const handleSuccessFulStripeBid = () => {
  const item = storeMarketplace.selectedItem;
  if (auction.value) {
    gtm.trackSuccessfulBid({
      value: Math.ceil(bidAmountFiat.value * 100),
      auction_id: auction.value.cuid,
      token_id: item.tokenId,
      currency: currency.value as Currency,
      username: storeUser.username,
      user_id: storeUser.cuid,
      market:
        auction.value.marketType === MarketType.SECONDARY
          ? MarketType.SECONDARY
          : MarketType.PRIMARY,
      nft_type: item.cardType,
      payment_method: "stripe",
    });
  }

  storeMarketplace.bidInProgress = false;
  storeApp.setBidModalState(false);
};

async function bidWithStripe() {
  if (!checkoutRef.value || isNaN(bidAmountFiat.value) || fiatError.value || !auction.value) {
    return;
  }

  if (BigNumber.from(Math.ceil(bidAmountFiat.value * 100)).gt(Number.MAX_SAFE_INTEGER - 1)) {
    throw new Error("Bid amount too high");
  }

  storeMarketplace.bidInProgress = true;

  await stripe.launchCheckoutSession({
    ref: checkoutRef as Ref<HTMLElement>,
    auctionCuid: auction.value.cuid,
    amountInCents: Math.ceil(purchaseInfo.value.bidAmount * 100),
    currency: purchaseInfo.value.currency,
    creditAmount: purchaseInfo.value.credits.amount,
    successCallback: () => handleSuccessFulStripeBid(),
    errorCallback: () => {
      storeMarketplace.bidInProgress = false;
      polPrice.refreshRates();
      errorCrypto.value = "Something went wrong";
    },
  });
}

function handleStripeModalBackButton() {
  stripe.cancelCheckoutSession();
  storeMarketplace.bidInProgress = false;
}

const emit = defineEmits<{
  (event: "onClose"): void;
}>();

const isInputFocused = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
function focusInput() {
  inputRef.value?.focus();
}

const payments = computed(() => {
  const methods = [
    {
      method: PaymentMethod.CREDIT_CARD,
      label: t("_payment.credit-card"),
    },
    {
      method: PaymentMethod.CRYPTO,
      label: t("_shared.wallet"),
    },
  ];

  if (marketType.value === MarketType.SECONDARY) {
    return methods.filter((method) => method.method === PaymentMethod.CRYPTO);
  }
  return methods;
});

const handleCloseModal = () => {
  if (stripe.checkoutSessionInProgress) {
    handleStripeModalBackButton();
  } else if (showModalDeposit.value) {
    // Ignore closing modal if deposit modal is open
  } else {
    // creditsToUse.value = 0;
    emit("onClose");
  }
};

const disableSecondaryMarket = computed(() => {
  const users = [
    "cm8372y5g00000fs6ggj2971j",
    "cm7226qct000z0fs64zqn5t4q",
    "cm7kg0dnv00000fs68brc5l6j",
    "cm6tpozba00000fs6882w9zrs",
    "cm89u7fkq00010fs6gcp7cqlq",
    "cm8d53cp500000fs65dv4cf86",
    "cm6s4t1qw00010fs653bzc7ec",
    "clyxj82xl005k0hs68po79gd6",
    "cm5r01imf00380es68x0vfm1p",
    "cmawbiyec00020gs6d0qkbgrb",
    "cma9nl82z00010gs647c50l12",
  ];
  const userIsMatch = users.includes(storeUser.cuid);
  const isSecondary = marketType.value === MarketType.SECONDARY;
  if (isSecondary && userIsMatch) {
    return true;
  }
  return false;
});

const creditsInfo = computed(() => {
  const oneUsdInEuro = (polPrice.rates[Currency.EUR] / polPrice.rates[Currency.USD]) * 1;
  const oneUsdInWpol = parseFloat(polPrice.currencyToPol(1, Currency.USD).toFixed(2));
  const rates = {
    [Currency.EUR]: parseFloat(oneUsdInEuro.toFixed(2)),
    [Currency.WPOL]: oneUsdInWpol,
    [Currency.WMATIC]: oneUsdInWpol,
    [Currency.POL]: oneUsdInWpol,
    [Currency.USD]: 1,
    [Currency.USDC]: 1,
  };
  return {
    balance: storeUser.creditBalance,
    amount: creditsToUse.value,
    amountInCurrency: creditsToUse.value * rates[currency.value],
    currency: currency.value,
    rates,
  };
});

const finalAmount = computed(() => {
  const amount = bidAmountInSelectedCurrency.value - creditsInfo.value.amountInCurrency;
  return roundToDecimals(Math.max(0, amount), 2);
});

onBeforeMount(() => {
  polPrice.refreshRates();
});

const setBidToMinimum = () => {
  bidAmountInWpol.value = purchaseInfo.value.minAmount[Currency.WPOL];
  bidAmountInUsd.value = purchaseInfo.value.minAmount[Currency.USD];
  bidAmountInEur.value = purchaseInfo.value.minAmount[Currency.EUR];
};
</script>

<template>
  <Modal
    modalClasses="!p-0"
    size="4xl"
    :show="true"
    @on-close="handleCloseModal"
    :show-close-button="false"
  >
    <ModalDeposit v-if="showModalDeposit" :show="showModalDeposit" @onClose="closeModalDeposit" />
    <div
      v-show="stripe.checkoutSessionInProgress"
      class="bg-[#fff] p-4 text-slate-950 rounded-md w-full"
    >
      <div>
        <div class="font-semibold text-2xl text-center flex flex-row justify-between">
          <div>{{ title }}</div>
          <button
            @click="handleStripeModalBackButton"
            class="p-2 rounded-full hover:bg-black-800 bg-black border-[1px] border-neutral-600"
          >
            <X class="h-4 w-4 fill-white text-white" />
          </button>
        </div>
        <OvTicker
          v-if="auction"
          :cuid="auction.cuid"
          :end-date="auction.endedAt"
          :status="auction.status"
          class="self-end text-sm [&_span]:text-neutral-500 stroke-neutral-500"
        />
      </div>
      <div class="stripe-checkout-container pt-2" ref="checkoutRef"></div>
      <div class="flex justify-center items-center pt-4">
        <Button @click="handleStripeModalBackButton" type="secondary">
          <div class="flex gap-2 text-sm items-center">
            <ArrowLeft class="h-4 w-4" />
            <span>
              {{ $t("modals.bid.back") }}
            </span>
          </div>
        </Button>
      </div>
    </div>
    <div v-show="!stripe.checkoutSessionInProgress" class="grid grid-cols-2 md:grid-cols-3">
      <div class="justify-center hidden md:flex">
        <AuctionItem
          :background="false"
          :item="storeMarketplace.selectedItem"
          :is-interactive="false"
          :show-card-details="false"
          :useCurrency="currency"
          :showBidOrBuyBtn="false"
        />
      </div>
      <div class="flex flex-col gap-4 col-span-2 md:px-10">
        <div class="font-semibold text-2xl text-center pb-10 flex flex-row justify-between pt-5">
          <div>{{ title }}</div>
          <button
            @click.stop="handleCloseModal"
            class="p-2 rounded-full hover:bg-slate-800 bg-black border-[1px] border-slate-600"
          >
            <X class="h-4 w-4" />
          </button>
        </div>

        <div class="flex flex-row gap-4 md:hidden">
          <img class="h-32 object-contain" :src="storeMarketplace.selectedItem.image" alt="" />

          <div
            class="bg-neutral-800 items-center justify-center rounded-md w-full gap-2 flex flex-col text-xs"
          >
            <CardDataFooterSkeleton>
              <CardDataFooterScore
                v-if="storeMarketplace.selectedItem.cardType !== CardType.NftEquipment"
                :l10="(storeMarketplace.selectedItem as NftSkill).score"
                :l5="(storeMarketplace.selectedItem as NftSkill).l5"
                :court="(storeMarketplace.selectedItem as NftSkill).courtType"
                :skill="(storeMarketplace.selectedItem as NftSkill).skill"
                :scarcity="(storeMarketplace.selectedItem as NftSkill).scarcity"
                :season="(storeMarketplace.selectedItem as NftSkill).season"
                :xp="(storeMarketplace.selectedItem as NftSkill).xp"
              />

              <CardDataFooterPrice
                v-if="auction"
                :auction="auction"
                :scarcity="(storeMarketplace.selectedItem as NftSkill).scarcity"
              />
            </CardDataFooterSkeleton>
          </div>
        </div>
        <div class="flex flex-row justify-between w-full md:w-auto">
          <div class="flex flex-row gap-2 items-center w-full md:w-fit">
            <LastBids
              v-if="isPrimaryMarket && auction"
              :market-type="auction.marketType"
              :count="auction.countOrders"
              :currency-symbol="currencySymbol"
              :payment-method="paymentMethod"
              :currency="currency"
            />
            <div
              v-else-if="auction?.marketType === MarketType.SECONDARY"
              class="w-full md:w-fit justify-between rounded-full border-500 border-neutral-900 border px-3 p-1 text-xs flex flex-row items-center gap-2 pr-3"
            >
              {{
                t("_payment.sold-by", {
                  username: storeMarketplace.selectedItem.ownedBy?.username,
                })
              }}
            </div>
            <LastSales
              v-if="auction"
              :market-type="auction.marketType"
              :count="auction.countOrders"
              :currency-symbol="currencySymbol"
              :payment-method="paymentMethod"
              :currency="currency"
              :card="storeMarketplace.selectedItem"
            />
          </div>
          <div
            class="w-fit rounded-full border-500 border-neutral-900 border px-3 p-1 hidden md:block"
          >
            <OvTicker
              v-if="auction"
              :cuid="auction.cuid"
              :end-date="auction.endedAt"
              :status="auction.status"
              class="self-end text-xs [&_span]:text-neutral-200"
            />
          </div>
        </div>

        <div class="h-[1px] w-full bg-neutral-800"></div>
        <div class="grid 2 w-full gap-4" :class="`grid-cols-${payments.length}`">
          <div
            v-for="payment in payments"
            :key="payment.method"
            @click="handlePaymentMethodChange(payment.method)"
            class="border transition-colors p-3 text-sm text-neutral-200 border-neutral-700 rounded-md flex flex-col gap-1 cursor-pointer"
            :class="[
              isPrimaryMarket && 'hover:border-blue-700',
              paymentMethod === payment.method &&
                isPrimaryMarket &&
                '!border-blue-700 !bg-blue-950/70 ',
            ]"
          >
            <div
              class="flex gap-2 items-center text-xs"
              v-if="payment.method === PaymentMethod.CREDIT_CARD"
            >
              <icon-fg-credit-card class="w-6 h-6 fill-white" />
            </div>
            <icon-fg-w-pol-square
              v-else-if="
                payment.method === PaymentMethod.CRYPTO && !storeUser.migrationToUSDCCompleted
              "
              class="w-6 h-6 fill-white"
            />
            <div
              v-else-if="
                payment.method === PaymentMethod.CRYPTO && storeUser.migrationToUSDCCompleted
              "
              class="h-4 w-7 text-black bg-white rounded-sm flex justify-center items-center text-xs my-1"
            >
              $
            </div>

            <div class="flex text-xs flex-row flex-wrap md:text-sm w-full">
              <div class="pr-2">
                {{ payment.label }}
              </div>
              <template v-if="payment.method === PaymentMethod.CRYPTO && isLogged">
                <div
                  class="flex flex-nowrap flex-row text-yellow-500 gap-0.5"
                  v-if="!storeUser.migrationToUSDCCompleted"
                >
                  <div>{{ storeUser.wpolRemaining.toFixed(2) }}</div>
                  <div>WPOL</div>
                </div>
                <div
                  class="flex flex-nowrap flex-row text-yellow-500 gap-0.5"
                  v-else-if="storeUser.migrationToUSDCCompleted"
                >
                  <div>{{ storeUser.usdcRemaining.toFixed(2) }}</div>
                  <div>USDC</div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- BID INPUT -->
        <div class="flex flex-col gap-4 mb-6">
          <div
            @click.stop="focusInput"
            class="border border-transparent transition-colors px-3 py-4 gap-1 items-center text-neutral-200 bg-neutral-800 cursor-text rounded-md group"
            :class="[
              isInputFocused && '!border-blue-700 ',
              isBelowMinAmount.isBelow && '!border-red-800',
              !isBelowMinAmount.isBelow &&
                insufficientCryptoFunds &&
                paymentMethod === PaymentMethod.CRYPTO &&
                '!border-yellow-700',
              isPrimaryMarket && 'hover:border-blue-700/50',
              marketType === MarketType.SECONDARY && 'hover:cursor-not-allowed',
            ]"
          >
            <div class="flex">
              <div class="flex flex-row items-center gap-2">
                <icon-fg-credit-card
                  v-if="paymentMethod === PaymentMethod.CREDIT_CARD"
                  class="w-5 h-5 fill-white"
                />
                <div
                  v-else-if="storeUser.migrationToUSDCCompleted"
                  class="h-4 w-6 text-black bg-white rounded-sm flex justify-center items-center text-xs my-1"
                >
                  $
                </div>
                <icon-fg-w-pol-square v-else class="w-5 h-5 fill-white" />
                <div class="text-sm text-nowrap">
                  <div v-if="marketType !== MarketType.SECONDARY">
                    {{ t("_payment.your-bid") }}
                  </div>
                  <div v-else>
                    {{ t("_payment.purchase-price") }}
                  </div>
                </div>
              </div>

              <input
                v-if="paymentMethod === PaymentMethod.CREDIT_CARD && currency === Currency.EUR"
                ref="inputRef"
                @focus="isInputFocused = true"
                @blur="isInputFocused = false"
                @input="(e) => updateBidsInAllCurrencies(e, Currency.EUR)"
                min="0"
                class="text-right font-semibold text-xl"
                :class="[marketType === MarketType.SECONDARY && 'group-hover:cursor-not-allowed']"
                v-model="bidAmountInEur"
                type="number"
                :disabled="disableBidInput"
              />
              <input
                v-else-if="
                  (paymentMethod === PaymentMethod.CREDIT_CARD && currency === Currency.USD) ||
                  (paymentMethod === PaymentMethod.CRYPTO && storeUser.migrationToUSDCCompleted)
                "
                ref="inputRef"
                @focus="isInputFocused = true"
                @blur="isInputFocused = false"
                @input="(e) => updateBidsInAllCurrencies(e, Currency.USD)"
                min="0"
                class="text-right font-semibold text-xl"
                :class="[marketType === MarketType.SECONDARY && 'group-hover:cursor-not-allowed']"
                v-model="bidAmountInUsd"
                type="number"
                :disabled="disableBidInput"
              />
              <input
                v-else-if="
                  (paymentMethod === PaymentMethod.CRYPTO && !storeUser.migrationToUSDCCompleted) ||
                  !purchaseInfo.marketPlace.isPrimary
                "
                ref="inputRef"
                @focus="isInputFocused = true"
                @blur="isInputFocused = false"
                @input="(e) => updateBidsInAllCurrencies(e, Currency.WPOL)"
                min="0"
                class="text-right font-semibold text-xl"
                :class="[marketType === MarketType.SECONDARY && 'group-hover:cursor-not-allowed']"
                v-model="bidAmountInWpol"
                type="number"
                :disabled="disableBidInput"
              />

              <div
                ref="currencyDropdown"
                :style="[
                  floatingStyles,
                  { visibility: showCurrencyDropdown ? 'visible' : 'hidden' },
                ]"
                class="bg-white text-neutral-800 p-1 rounded-md z-50 flex flex-col gap-1"
              >
                <div
                  v-for="item in [Currency.EUR, Currency.USD]"
                  :key="item"
                  class="hover:bg-blue-700 hover:text-white rounded-md p-2 cursor-pointer"
                  :class="[item === currency ? 'bg-blue-700 text-white' : '']"
                  @click="updateCreditCardCurrency(item)"
                >
                  {{ item.toUpperCase() }}
                </div>
              </div>
              <div
                @click.stop="toggleCurrencyDropdown"
                ref="currencyDropdownRef"
                v-if="paymentMethod === PaymentMethod.CREDIT_CARD"
                class="font-medium border-l border-neutral-700 pl-2 ml-2 flex flex-row items-center gap-1 cursor-pointer group text-xl"
              >
                {{ currencySymbol }}
                <ChevronDown class="w-4 h-4 text-neutral-500 group-hover:text-white" />
              </div>
              <div
                v-else-if="
                  !storeUser.migrationToUSDCCompleted ||
                  (marketType === MarketType.SECONDARY && !storeUser.migrationToUSDCCompleted)
                "
                class="font-medium ml-1 text-xl flex items-center pl-1.5"
              >
                <icon-fg-w-pol class="w-5 h-5" />
              </div>
              <div v-else class="font-medium ml-1 text-xl">$</div>
            </div>
            <div
              v-if="isBelowMinAmount.isBelow && marketType === MarketType.PRIMARY"
              class="bg-red-400/20 rounded-md flex w-full justify-between p-2 items-center font-medium mt-4"
            >
              <div class="text-xs text-red-400 flex flex-row gap-2 items-center font-medium">
                <Info class="h-4 w-4" />
                {{
                  t("_payment.below-min-amount", {
                    amount: isBelowMinAmount.minAmount,
                    currency: currency.toUpperCase(),
                  })
                }}
              </div>
              <Button @click="setBidToMinimum" size="xs" type="primary-white"
                ><span class="font-semibold">{{ t("_payment.set-min") }}</span></Button
              >
            </div>
            <div
              v-else-if="insufficientCryptoFunds && isLogged"
              class="bg-yellow-400/20 rounded-md flex w-full justify-between p-2 items-center font-medium mt-4"
            >
              <div class="text-xs text-yellow-400 flex flex-row gap-2 items-center">
                <Info class="h-4 w-4" /> {{ t("_payment.insufficient-funds") }}
              </div>
              <Button @click="addFund" size="xs" type="primary-white"
                ><span class="font-semibold">{{ t("_payment.add-funds") }}</span></Button
              >
            </div>
          </div>

          <ModalBidCredits
            v-if="marketType !== MarketType.SECONDARY && storeUser.creditBalance > 0"
            :currency="currency"
            :credit-rate-in-currency="creditsInfo.rates[currency]"
            :credit-balance="storeUser.creditBalance"
            v-model:creditsToUse="creditsToUse"
            :purchaseInfo="purchaseInfo"
          />

          <div class="h-[1px] w-full bg-neutral-800"></div>
          <Button
            :loading="storeMarketplace.bidInProgress"
            @click="bidOrBuy"
            type="primary-blue"
            class="w-full"
            :class="[disableSecondaryMarket && 'cursor-not-allowed']"
            :disabled="disableSecondaryMarket || insufficientCryptoFunds"
          >
            <template v-if="isLogged">
              {{ bidOrBuyConfirmation }}
            </template>
            <template v-else>
              {{ t("nav.login") }}
            </template>
          </Button>

          <div
            v-if="errorCrypto && paymentMethod === PaymentMethod.CRYPTO"
            class="border-red-600/2 bg-red-950 text-red-400 text-sm p-2 rounded-md text-center"
          >
            {{ errorCrypto }}
          </div>
          <div
            v-else-if="errorFiat && paymentMethod === PaymentMethod.CREDIT_CARD"
            class="border-red-600/2 bg-red-950 text-red-400 text-sm p-2 rounded-md text-center"
          >
            {{ errorFiat }}
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
input {
  @apply border-transparent bg-transparent outline-none w-full;
  box-shadow: none;
  border-radius: 0;
  &::placeholder {
    @apply text-neutral-500;
  }
}
</style>
